/**
 * WallpaperExploreCard — lean display card for the Explore → Wallpapers tab.
 *
 * Rebuilt from scratch to fix a mount-time freeze in the old WallpaperCard path.
 * This card intentionally does NOT include:
 *   - Admin edit/delete dialogs inline (delegated to the shared, zero-overhead
 *     <AdminEditableCard> wrapper, which renders children as-is for non-admins)
 *   - Long-press preview overlay / hooks
 *   - A duplicate `new Image()` preloader (the <img> tag already loads the image)
 *   - Per-card realtime subscriptions (views, creator profile/avatar)
 *   - framer-motion — hover/tap feedback is plain CSS, identical on every card
 *
 * Just an image in an aspect-ratio box (no layout thrash), a static bottom
 * scrim for text legibility, title/category, and a purchased badge.
 */

import React, { memo } from 'react';
import { Check } from 'lucide-react';
import type { WallpapersResponse } from '@/lib/collections/wallpapers';
import { useImageLoaded } from '@/hooks/use-image-loaded';

interface WallpaperExploreCardProps {
  wallpaper: WallpapersResponse;
  isPurchased: boolean;
  onClick: () => void;
}

export const WallpaperExploreCard: React.FC<WallpaperExploreCardProps> = memo(function WallpaperExploreCard({
  wallpaper,
  isPurchased,
  onClick,
}) {
  const { ref: imgRef, loaded, onLoad, onError } = useImageLoaded(wallpaper.thumbnailUrl || wallpaper.imageUrl);

  return (
    <div
      onClick={onClick}
      onContextMenu={(e) => e.preventDefault()}
      className="wallpaper-explore-card wallpaper-protected"
      style={{
        position: 'relative',
        borderRadius: 14,
        overflow: 'hidden',
        aspectRatio: '9 / 14',
        background: '#141420',
        cursor: 'pointer',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
        // Lets the browser skip layout/paint work for cards far outside the
        // viewport instead of a JS windowing library measuring every item.
        contentVisibility: 'auto',
        containIntrinsicSize: '160px 260px',
      } as React.CSSProperties}
    >
      {wallpaper.imageUrl && (
        <img
          ref={imgRef}
          src={wallpaper.thumbnailUrl || wallpaper.imageUrl}
          alt={wallpaper.title}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={onLoad}
          onError={onError}
          onDragStart={(e) => e.preventDefault()}
          className="protected-image"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.18s ease-out',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            pointerEvents: 'none',
          } as React.CSSProperties}
        />
      )}

      {/* Static bottom scrim for text legibility — no animation */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: '48%',
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.75) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Purchased badge. The price badge that used to sit here was removed —
           price is shown in the full viewer / purchase flow, not on the
           preview grid tiles. */}
      {isPurchased && (
        <div
          style={{
            position: 'absolute', top: 8, right: 8,
            width: 22, height: 22, borderRadius: '50%',
            background: '#22c55e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Check size={12} color="#fff" strokeWidth={2.5} />
        </div>
      )}

      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '24px 10px 10px',
      }}>
        <span style={{
          display: 'block',
          fontSize: 14, fontWeight: 700, color: '#ffffff',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          fontFamily: "'Syne', sans-serif",
          letterSpacing: '0.01em',
        }}>
          {wallpaper.title}
        </span>
        {wallpaper.category && (
          <span style={{
            display: 'block',
            fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.55)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: "'Syne', sans-serif",
            letterSpacing: '0.01em',
          }}>
            {wallpaper.category}
          </span>
        )}
      </div>
    </div>
  );
});

WallpaperExploreCard.displayName = 'WallpaperExploreCard';

export default WallpaperExploreCard;
